// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Plus, Loader2, Save, Trash2, GripVertical, ChevronLeft, Search, Check } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABELS = { junior: "Junior", confirmed: "Confirmé", senior: "Senior", expert: "Expert" };
const STATUS_OPTIONS = { draft: "Brouillon", published: "Publié", archived: "Archivé" };

export default function InterviewTemplatePage() {
  const { workspaceId, templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionDialog, setQuestionDialog] = useState(null); // section index
  const [searchQ, setSearchQ] = useState("");
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  useEffect(() => {
    loadData();
  }, [templateId]);

  const loadData = async () => {
    try {
      const [tRes, qRes] = await Promise.all([
        interviewAPI.listTemplates(workspaceId),
        interviewAPI.listQuestions(workspaceId, { limit: 500 }),
      ]);
      const found = tRes.data.find(t => t.id === templateId);
      if (!found) { toast.error("Template non trouvé"); navigate(-1); return; }
      setTemplate(found);
      setQuestions(qRes.data);
    } catch (e) { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!template.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      await interviewAPI.updateTemplate(templateId, template);
      toast.success("Template sauvegardé");
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const addSection = () => {
    const sections = [...(template.sections || [])];
    sections.push({
      id: crypto.randomUUID(),
      title: "Nouvelle section",
      domain: "",
      weight: 1.0,
      is_mandatory: true,
      duration_minutes: 15,
      question_ids: [],
      order: sections.length,
    });
    setTemplate({ ...template, sections });
  };

  const updateSection = (idx, field, value) => {
    const sections = [...template.sections];
    sections[idx] = { ...sections[idx], [field]: value };
    setTemplate({ ...template, sections });
  };

  const removeSection = (idx) => {
    const sections = template.sections.filter((_, i) => i !== idx);
    setTemplate({ ...template, sections });
  };

  const openQuestionPicker = (sectionIdx) => {
    setQuestionDialog(sectionIdx);
    setSearchQ("");
    const currentIds = template.sections[sectionIdx].question_ids || [];
    setFilteredQuestions(questions.filter(q => !currentIds.includes(q.id)));
  };

  const addQuestionToSection = (sectionIdx, questionId) => {
    const sections = [...template.sections];
    const qids = [...(sections[sectionIdx].question_ids || [])];
    if (!qids.includes(questionId)) {
      qids.push(questionId);
      sections[sectionIdx] = { ...sections[sectionIdx], question_ids: qids };
      setTemplate({ ...template, sections });
    }
    setFilteredQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const removeQuestionFromSection = (sectionIdx, questionId) => {
    const sections = [...template.sections];
    sections[sectionIdx] = {
      ...sections[sectionIdx],
      question_ids: sections[sectionIdx].question_ids.filter(id => id !== questionId),
    };
    setTemplate({ ...template, sections });
  };

  const getQuestionText = (qid) => {
    const q = questions.find(q => q.id === qid);
    return q ? q.question_text : "Question inconnue";
  };

  const getQuestionDomain = (qid) => {
    const q = questions.find(q => q.id === qid);
    return q?.domain || "";
  };

  if (loading || !template) {
    return <WorkspaceLayout><div className="page-container"><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div></WorkspaceLayout>;
  }

  const totalQuestions = template.sections.reduce((acc, s) => acc + (s.question_ids?.length || 0), 0);
  const totalDuration = template.sections.reduce((acc, s) => acc + (s.duration_minutes || 0), 0);

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews/templates`)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">Éditeur de template</h1>
            <p className="text-sm text-muted-foreground">{totalQuestions} questions · {totalDuration} min</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Sauvegarder
          </Button>
        </div>

        {/* Template Info */}
        <Card className="mb-6">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Titre *</Label><Input value={template.title} onChange={(e) => setTemplate({...template, title: e.target.value})} /></div>
              <div><Label>Poste visé</Label><Input value={template.target_role} onChange={(e) => setTemplate({...template, target_role: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Textarea value={template.description} onChange={(e) => setTemplate({...template, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Niveau cible</Label>
                <Select value={template.target_level} onValueChange={(v) => setTemplate({...template, target_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEVEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Durée globale (min)</Label><Input type="number" value={template.duration_minutes} onChange={(e) => setTemplate({...template, duration_minutes: parseInt(e.target.value) || 60})} /></div>
              <div>
                <Label>Statut</Label>
                <Select value={template.status} onValueChange={(v) => setTemplate({...template, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_OPTIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sections ({template.sections?.length || 0})</h2>
          <Button variant="outline" className="gap-2" onClick={addSection}><Plus className="h-4 w-4" />Ajouter une section</Button>
        </div>

        <div className="space-y-4">
          {(template.sections || []).map((section, idx) => (
            <Card key={section.id || idx}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
                  <div className="flex-1">
                    <Input value={section.title} className="text-lg font-semibold border-none p-0 h-auto focus-visible:ring-0"
                      onChange={(e) => updateSection(idx, "title", e.target.value)} placeholder="Nom de la section" />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeSection(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div><Label className="text-xs">Domaine</Label><Input value={section.domain} onChange={(e) => updateSection(idx, "domain", e.target.value)} placeholder="Linux, K8s..." /></div>
                  <div><Label className="text-xs">Poids</Label><Input type="number" step="0.1" value={section.weight} onChange={(e) => updateSection(idx, "weight", parseFloat(e.target.value) || 1)} /></div>
                  <div><Label className="text-xs">Durée (min)</Label><Input type="number" value={section.duration_minutes} onChange={(e) => updateSection(idx, "duration_minutes", parseInt(e.target.value) || 15)} /></div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={section.is_mandatory} onCheckedChange={(v) => updateSection(idx, "is_mandatory", v)} />
                      <Label className="text-xs">Obligatoire</Label>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Questions in section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{(section.question_ids || []).length} question(s)</p>
                    <Button variant="outline" size="sm" onClick={() => openQuestionPicker(idx)} className="gap-1">
                      <Plus className="h-3 w-3" />Ajouter
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {(section.question_ids || []).map((qid, qi) => (
                      <div key={qid} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
                        <span className="text-muted-foreground font-mono text-xs w-6">{qi + 1}.</span>
                        <span className="flex-1 truncate">{getQuestionText(qid)}</span>
                        {getQuestionDomain(qid) && <Badge variant="outline" className="text-xs">{getQuestionDomain(qid)}</Badge>}
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeQuestionFromSection(idx, qid)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {(template.sections || []).length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center py-12">
              <p className="text-muted-foreground">Ajoutez des sections pour structurer votre entretien</p>
              <Button className="mt-4" onClick={addSection}>Ajouter une section</Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Question Picker Dialog */}
      <Dialog open={questionDialog !== null} onOpenChange={() => setQuestionDialog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Ajouter des questions</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="pl-10" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 mt-2">
            {filteredQuestions
              .filter(q => !searchQ || q.question_text.toLowerCase().includes(searchQ.toLowerCase()) || q.domain.toLowerCase().includes(searchQ.toLowerCase()))
              .slice(0, 50)
              .map(q => (
                <div key={q.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => addQuestionToSection(questionDialog, q.id)}>
                  <Plus className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{q.question_text}</p>
                    <div className="flex gap-1 mt-0.5">
                      {q.domain && <Badge variant="outline" className="text-[10px] h-4">{q.domain}</Badge>}
                      <Badge variant="secondary" className="text-[10px] h-4">{q.difficulty}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            {filteredQuestions.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Toutes les questions ont été ajoutées</p>}
          </div>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
